/* eslint-disable react-hooks/exhaustive-deps */
import { Space, Button, Table, Tooltip, Tag } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";

import { showErrorNotification, showSuccessNotification } from "../../utils/notification";
import { clamp } from "../../utils/responsiveHelpers";
import SurveyQuestionCardRender from "../common-utils/SurveyQuestionCardRender";
import FormBuilderModal from "./CreateFormModal/FormBuilderModal";
import DetailedSurveyResponses from "./DetailedSurveyResponseModal";
import SurveyResponseModal from "./SurveyResponseModal";

const SurveySystemDash = ({ adminConfig, ROOT_URL }) => {
  const { surveySystemDashboard = {} } = adminConfig;
  const { hasCreationAccess = false } = surveySystemDashboard;

  const [isSurveyCreationModalOpen, setIsSurveyCreationModalOpen] = useState(false);
  const [creationFormPrerequisites, setCreationFormPrerequisites] = useState({});
  const [surveyList, setSurveyList] = useState([]);
  const [formViewData, setFormViewData] = useState(false);
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [isDetailedSurveyResponseModalOpen, setIsDetailedSurveyResponseModalOpen] = useState(false);

  const [activeSurveyId, setActiveSurveyId] = useState();
  const [activeSurveyTitle, setActiveSurveyTitle] = useState("");
  const [isSurveyResponseModalOpen, setIsSurveyResponseModalOpen] = useState(false);
  const [aggregatedSurveyResponses, setAggregatedSurveyResponses] = useState({});

  const fetchSurveyCreationFormPrerequisites = () => {
    axios
      .get(`${ROOT_URL}/admin/survey-system/form-requisites`)
      .then(({ data }) => {
        setCreationFormPrerequisites(data);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const fetchSurveyList = () => {
    axios
      .get(`${ROOT_URL}/admin/survey-system/list-surveys`)
      .then(({ data }) => {
        const { surveyList } = data;
        setSurveyList(surveyList);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleSurveyCreation = (surveyParams) => {
    axios
      .post(`${ROOT_URL}/admin/survey-system/create-survey`, {
        surveyParams
      })
      .then(() => {
        showSuccessNotification("Survey Created Successfully");
        setIsSurveyCreationModalOpen(false);
        fetchSurveyList();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const toggleSurveyActiveState = (surveyId, activeState) => {
    axios
      .post(`${ROOT_URL}/admin/survey-system/toggle-active-state`, {
        surveyId,
        activeState
      })
      .then(() => {
        showSuccessNotification(
          `Survey ${!activeState ? "de-activated" : "activated"} successfully.`
        );
        fetchSurveyList();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleArchivingSurvey = (surveyId) => {
    axios
      .post(`${ROOT_URL}/admin/survey-system/archive-survey`, { surveyId })
      .then(() => {
        showSuccessNotification("Survey archived successfully.");
        fetchSurveyList();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleFormView = () => {
    setFormViewData(true);
  };

  const fetchAggregatedSurveyResponses = (surveyId) => {
    setActiveSurveyId(surveyId);
    axios
      .get(`${ROOT_URL}/admin/survey-system/response-analytics/`, {
        params: {
          surveyId
        }
      })
      .then(({ data }) => {
        const { questionToAggregatedResponses } = data;
        setAggregatedSurveyResponses(questionToAggregatedResponses);
        setIsSurveyResponseModalOpen(true);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index",
      width: 70
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 220
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 260,
      ellipsis: {
        showTitle: false
      },
      render: (description) => (
        <Tooltip placement="topLeft" title={description}>
          {description}
        </Tooltip>
      )
    },
    {
      title: "Link",
      dataIndex: "surveyLink",
      key: "surveyLink",
      width: 200
    },
    {
      title: "Total Responses",
      dataIndex: "totalResponses",
      key: "totalResponses",
      width: 150
    },
    {
      title: "Scope",
      dataIndex: "scope",
      key: "scope",
      width: 160,
      sorter: (a, b) => {
        if (typeof a.scope === "string" && typeof b.scope === "string") {
          return a.scope?.localeCompare(b.scope) | 0;
        }
        // Fallback comparison if not string (handle the case if one or both values are not strings)
        return 0;
      }
    },
    {
      title: "Created By",
      dataIndex: "adminName",
      key: "adminName",
      width: 150
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (row) => {
        let statusObj = {
          color: row.isActive ? "green" : "red",
          text: row.isActive ? "ACTIVE" : "NOT-ACTIVE"
        };
        return (
          <Tag color={statusObj.color} key={statusObj.text}>
            {statusObj.text.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: "Actions",
      key: "action",
      width: 220,
      render: (row) => (
        <Space wrap style={{ width: "100%" }}>
          <Button
            type="primary"
            disabled={row.archived}
            danger={row.isActive}
            onClick={() => {
              toggleSurveyActiveState(row.key, !row.isActive);
            }}
          >
            {row.isActive ? "Deactivate" : "Activate"}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              handleArchivingSurvey(row.key);
            }}
          >
            Mark Archived
          </Button>
          <Button
            type="primary"
            onClick={() => {
              fetchAggregatedSurveyResponses(row.key);
            }}
          >
            View Responses Analytics
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setActiveSurveyId(row.key);
              setActiveSurveyTitle(row.title);
              setIsDetailedSurveyResponseModalOpen(true);
            }}
          >
            View Detailed Responses
          </Button>
          <Button type="primary" onClick={() => handleFormView()}>
            View Form
          </Button>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchSurveyCreationFormPrerequisites();
    fetchSurveyList();
  }, []);

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 20),
        marginRight: clamp(12, 20)
      }}
    >
      {hasCreationAccess && (
        <div>
          <Button
            type="primary"
            onClick={() => {
              setIsSurveyCreationModalOpen(true);
            }}
          >
            Create Survey
          </Button>
        </div>
      )}

      <br />

      <Table
        columns={columns}
        dataSource={surveyList || []}
        bordered
        scroll={{ x: 1300 }}
        pagination={false}
      />

      {isSurveyCreationModalOpen === true && (
        <FormBuilderModal
          isModalOpen={isSurveyCreationModalOpen}
          setIsModalOpen={setIsSurveyCreationModalOpen}
          creationFormPrerequisites={creationFormPrerequisites}
          handleSurveyCreation={handleSurveyCreation}
        />
      )}

      {formViewData && (
        <SurveyQuestionCardRender
          surveyQuestions={surveyQuestions}
          setSurveyQuestions={setSurveyQuestions}
          showSuccessNotification={showSuccessNotification}
        />
      )}

      {isSurveyResponseModalOpen === true && (
        <SurveyResponseModal
          isModalOpen={isSurveyResponseModalOpen}
          setIsModalOpen={setIsSurveyResponseModalOpen}
          aggregatedSurveyResponses={aggregatedSurveyResponses}
          activeSurveyId={activeSurveyId}
        />
      )}

      {isDetailedSurveyResponseModalOpen === true && (
        <DetailedSurveyResponses
          isModalOpen={isDetailedSurveyResponseModalOpen}
          setIsModalOpen={setIsDetailedSurveyResponseModalOpen}
          surveyId={activeSurveyId}
          ROOT_URL={ROOT_URL}
          surveyTitle={activeSurveyTitle}
        />
      )}
    </div>
  );
};

export default SurveySystemDash;
